import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Dropdown, Popup } from 'semantic-ui-react';
import { ResourceInputField } from 'common/components/atoms/ResourceDetails';
import { WaveSquare, TemperatureHalf, Magnet, Times } from 'common/images/FaIcons';
import { useTranslation } from 'react-i18next';
import { getAxisDirectionLabels } from '../../utils/directionLabels';
import {
  isEventMetricAvailable,
  isEventOperatorAvailable,
  operatorResetForMetricType
} from '../../utils/eventMetricAvailability';
import { formatDecimalDisplay } from 'common/utils';
import {
  translateEventDetectionSensorType,
  translateEventDetectionMetricType,
} from '../../utils/sensorMeasurementDisplay';
import {
  ConditionRowContainer,
  ConditionInputGroup,
  ConditionLabel,
  ConditionUnit,
  ThresholdInputWrapper,
  RemoveButton,
  DropdownContainer,
  IconText,
  AxisSelectorContainer,
  AxisToggleButtonGroup,
  AxisToggleButton,
  RangeColumnHeader,
  LinkedBadge
} from './EventDetection.styled';

const FALLBACK_OPERATORS = [
  { key: 'gt', text: '>', value: '>' },
  { key: 'gte', text: '≥', value: '>=' },
  { key: 'lt', text: '<', value: '<' },
  { key: 'lte', text: '≤', value: '<=' },
  { key: 'eq', text: '=', value: '=' },
  { key: 'outside', text: 'Outside Range', value: 'OUTSIDE' },
  { key: 'inside', text: 'Inside Range', value: 'INSIDE' }
];


// Icon mapping for sensor types
const SENSOR_TYPE_ICONS = {
  Vibration: WaveSquare,
  Temperature: TemperatureHalf,
  Magnetometer: Magnet
};

/**
 * Transform metadata from backend into UI format
 */
const transformMetadataToUI = (t, metadata, temperatureOnly = false, magnetometerAvailable = true, currentMetric = null) => {
  // Fallback values (used when metadata is not available)
  const FALLBACK_METRICS = [
    { key: 'vibration', text: t('sensors.eventDetection.metrics.vibration', { defaultValue: 'Vibration' }), value: 'Vibration', icon: WaveSquare },
    { key: 'temperature', text: t('sensors.eventDetection.metrics.temperature', { defaultValue: 'Temperature' }), value: 'Temperature', icon: TemperatureHalf },
    { key: 'magnetometer', text: t('sensors.eventDetection.metrics.magnetometer', { defaultValue: 'Magnetometer' }), value: 'Magnetometer', icon: Magnet }
  ];

  const FALLBACK_METRIC_TYPES = {
    Vibration: [
      { key: 'accel_rms', text: t('sensors.eventDetection.metricTypes.accelRms', { defaultValue: 'Accel RMS' }), value: 'Accel RMS', unit: 'g', isTriaxial: true },
      { key: 'accel_peak', text: t('sensors.eventDetection.metricTypes.accelPeak', { defaultValue: 'Accel Peak' }), value: 'Accel Peak', unit: 'g', isTriaxial: true },
      { key: 'velocity_rms', text: t('sensors.eventDetection.metricTypes.velocityRms', { defaultValue: 'Velocity RMS' }), value: 'Velocity RMS', unit: 'mm/s', isTriaxial: true },
      { key: 'velocity_peak', text: t('sensors.eventDetection.metricTypes.velocityPeak', { defaultValue: 'Velocity Peak' }), value: 'Velocity Peak', unit: 'mm/s', isTriaxial: true }
    ],
    Temperature: [
      { key: 'overall', text: t('sensors.eventDetection.metricTypes.overall', { defaultValue: 'Overall' }), value: 'Overall', unit: '°C', isTriaxial: false }
    ],
    Magnetometer: [
      { key: 'magnetic_flux', text: t('sensors.eventDetection.metricTypes.magneticFlux', { defaultValue: 'Magnetic Flux' }), value: 'Magnetic Flux', unit: 'mT', isTriaxial: false },
      { key: 'speed', text: t('sensors.eventDetection.metricTypes.speed', { defaultValue: 'Speed' }), value: 'Speed', unit: 'Hz', isTriaxial: false }
    ]
  };

  const isMetricAvailable = metric => isEventMetricAvailable(metric.value, {
    temperatureOnly,
    magnetometerAvailable,
    currentMetric
  });

  const availableFallbackMetrics = FALLBACK_METRICS.filter(isMetricAvailable);

  if (!metadata || !metadata.enums) {
    return {
      metrics: availableFallbackMetrics,
      metricTypes: FALLBACK_METRIC_TYPES,
      operators: FALLBACK_OPERATORS
    };
  }

  const { enums } = metadata;

  // Transform sensor types to metrics
  // Use name directly from backend (sensor types don't need ui_name since they're already simple)
  let metrics = (enums.sensor_types || []).map((sensorType) => {
    const icon = SENSOR_TYPE_ICONS[sensorType.name] || WaveSquare;
    return {
      key: sensorType.name.toLowerCase(),
      text: translateEventDetectionSensorType(t, sensorType.name),
      value: sensorType.name,
      icon
    };
  });

  metrics = metrics.filter(isMetricAvailable);

  // Transform parameters to metric types
  const metricTypes = {};
  if (enums.parameters) {
    Object.entries(enums.parameters).forEach(([sensorTypeKey, params]) => {
      // Find the sensor type that matches this parameter key
      // The key is lowercase (e.g., 'vibration', 'temperature', 'magnetometer')
      const sensorType = (enums.sensor_types || []).find(st =>
        st.name.toLowerCase() === sensorTypeKey
      );

      if (sensorType) {
        const sensorTypeName = sensorType.name;
        const isTriaxial = sensorType.is_multi_axis || false;

        metricTypes[sensorTypeName] = (params || []).map(param => ({
          // Use name directly from backend (now UI-friendly)
          key: param.name.toLowerCase().replace(/\s+/g, '_'),
          text: translateEventDetectionMetricType(t, param.name),
          value: param.name,
          unit: param.unit || '',
          isTriaxial
        }));
      }
    });
  }

  // Transform operators
  // Use name directly from backend (now UI-friendly)
  const operators = (enums.operators || []).map((op) => {
    // For range operators, use "Outside Range" / "Inside Range" for display text
    let displayText = op.name;
    if (op.name === 'OUTSIDE') {
      displayText = t('sensors.eventDetection.operators.outsideRange', { defaultValue: 'Outside Range' });
    } else if (op.name === 'INSIDE') {
      displayText = t('sensors.eventDetection.operators.insideRange', { defaultValue: 'Inside Range' });
    }

    return {
      key: op.name.toLowerCase().replace(/\s+/g, '_'),
      text: displayText,
      value: op.name
    };
  });

  return {
    metrics: metrics.length > 0 ? metrics : availableFallbackMetrics,
    metricTypes: Object.keys(metricTypes).length > 0 ? metricTypes : FALLBACK_METRIC_TYPES,
    operators: operators.length > 0 ? operators : FALLBACK_OPERATORS
  };
};

const ConditionRow = ({ condition, onUpdate, onRemove, showRemove, eventMetadata, vibrationDirectionPreference = 1, temperatureOnly = false, magnetometerAvailable = true, hasError = false, isCoupled = false }) => {
  const { t } = useTranslation();
  // Transform metadata to UI format
  const { metrics, metricTypes, operators: allOperators } = useMemo(
    () => transformMetadataToUI(t, eventMetadata, temperatureOnly, magnetometerAvailable, condition.metric),
    [eventMetadata, temperatureOnly, magnetometerAvailable, condition.metric]
  );
  // Speed hides Outside Range. Hidden, not rejected -- the backend still
  // accepts it, so a rule saved with it keeps working.
  const operators = useMemo(
    () => allOperators.filter(op => isEventOperatorAvailable(op.value, {
      metricType: condition.metricType,
      currentOperator: condition.operator
    })),
    [allOperators, condition.metricType, condition.operator]
  );
  const currentMetricTypeOptions = metricTypes[condition.metric] || [];
  const currentMetricType = currentMetricTypeOptions.find(
    t => t.value === condition.metricType
  ) || null;
  const isTriaxial = (currentMetricType && currentMetricType.isTriaxial) || false;
  const unit = (currentMetricType && currentMetricType.unit) || '';
  const isRangeOperator = condition.operator === 'OUTSIDE' || condition.operator === 'INSIDE';

  // Initialize axis - default to [0] for single-axis, [1,2,3] for triaxial
  const [selectedAxes, setSelectedAxes] = useState(() => {
    if (condition.axis) {
      return condition.axis;
    }
    return isTriaxial ? [1, 2, 3] : [0];
  });

  // Axis mapping - use direction labels based on account preference
  const axisMap = { 1: 'x', 2: 'y', 3: 'z' }; // Keep for internal threshold key mapping
  const axisLabels = useMemo(
    () => getAxisDirectionLabels(t, vibrationDirectionPreference),
    [t, vibrationDirectionPreference]
  );

  // Local state for threshold inputs to allow empty values during editing
  const getThresholdString = (thresholds, key) => {
    if (thresholds && thresholds[key] !== undefined && thresholds[key] !== null) {
      const formatted = formatDecimalDisplay(thresholds[key], 3);
      return formatted === '' ? '' : String(formatted);
    }
    return '';
  };

  // Helper to get threshold values for range operators
  const getRangeThresholds = (thresholds) => {
    if (isTriaxial) {
      // For triaxial, only include selected axes
      const result = {};
      selectedAxes.forEach((axisVal) => {
        const axisKey = axisMap[axisVal];
        result[`min${axisKey.toUpperCase()}`] = getThresholdString(thresholds && thresholds.min, axisKey);
        result[`max${axisKey.toUpperCase()}`] = getThresholdString(thresholds && thresholds.max, axisKey);
      });
      return result;
    }
    // For single-axis, expect { min: { value }, max: { value } }
    return {
      minValue: getThresholdString(thresholds && thresholds.min, 'value'),
      maxValue: getThresholdString(thresholds && thresholds.max, 'value')
    };
  };

  const [thresholdInputs, setThresholdInputs] = useState(() => {
    if (isRangeOperator) {
      return getRangeThresholds(condition.thresholds);
    }
    if (isTriaxial) {
      // Only include selected axes
      const result = {};
      selectedAxes.forEach((axisVal) => {
        const axisKey = axisMap[axisVal];
        result[axisKey] = getThresholdString(condition.thresholds, axisKey);
      });
      return result;
    }
    return {
      value: getThresholdString(condition.thresholds, 'value')
    };
  });

  // Update local state when condition thresholds or axis change externally
  useEffect(() => {
    if (condition.axis) {
      setSelectedAxes(condition.axis);
    }
    const isRange = condition.operator === 'OUTSIDE' || condition.operator === 'INSIDE';
    if (isRange) {
      setThresholdInputs(getRangeThresholds(condition.thresholds));
    } else if (isTriaxial) {
      // Only include selected axes
      const result = {};
      const currentAxes = condition.axis || selectedAxes;
      currentAxes.forEach((axisVal) => {
        const axisKey = axisMap[axisVal];
        result[axisKey] = getThresholdString(condition.thresholds, axisKey);
      });
      setThresholdInputs(result);
    } else {
      setThresholdInputs({
        value: getThresholdString(condition.thresholds, 'value')
      });
    }
  }, [condition.thresholds, condition.operator, condition.axis, isTriaxial, selectedAxes]);

  const handleMetricChange = useCallback((e, data) => {
    const typesForMetric = metricTypes[data.value] || [];
    const firstType = typesForMetric[0];
    if (firstType) {
      const newIsTriaxial = firstType.isTriaxial || false;
      const newAxis = newIsTriaxial ? [1, 2, 3] : [0];
      setSelectedAxes(newAxis);

      onUpdate({
        metric: data.value,
        metricType: firstType.value,
        axis: newAxis,
        thresholds: newIsTriaxial
          ? { x: 0, y: 0, z: 0 }
          : { value: 0 }
      });
    }
  }, [metricTypes, onUpdate]);

  const handleMetricTypeChange = useCallback((e, data) => {
    const typesForMetric = metricTypes[condition.metric] || [];
    const type = typesForMetric.find(t => t.value === data.value);
    const isNewTriaxial = (type && type.isTriaxial) || false;
    const isCurrentTriaxial = (currentMetricType && currentMetricType.isTriaxial) || false;

    // Update axis based on new metric type
    const newAxis = isNewTriaxial ? [1, 2, 3] : [0];
    setSelectedAxes(newAxis);

    // Preserve thresholds if structure is compatible (both triaxial or both single)
    let newThresholds;
    if (isNewTriaxial === isCurrentTriaxial) {
      // Same structure - preserve existing thresholds
      newThresholds = { ...condition.thresholds };
    } else if (isRangeOperator) {
      // Different structure - reset to zero
      // For range operators, need min and max
      newThresholds = isNewTriaxial
        ? { min: { x: 0, y: 0, z: 0 }, max: { x: 0, y: 0, z: 0 } }
        : { min: { value: 0 }, max: { value: 0 } };
    } else {
      newThresholds = isNewTriaxial ? { x: 0, y: 0, z: 0 } : { value: 0 };
    }

    const update = {
      metricType: data.value,
      axis: newAxis,
      thresholds: newThresholds
    };

    onUpdate({
      ...update,
      ...operatorResetForMetricType(condition.operator, data.value)
    });
  }, [metricTypes, condition.metric, condition.thresholds, condition.operator, currentMetricType, isRangeOperator, onUpdate]);

  const handleAxisChange = useCallback((axisValue) => {
    const newSelectedAxes = selectedAxes.includes(axisValue)
      ? selectedAxes.filter(a => a !== axisValue)
      : [...selectedAxes, axisValue].sort();

    // Ensure at least one axis is selected for triaxial
    if (isTriaxial && newSelectedAxes.length === 0) {
      return; // Don't allow deselecting all axes
    }

    setSelectedAxes(newSelectedAxes);

    // Update thresholds to only include selected axes
    const newThresholds = { ...condition.thresholds };

    if (isRangeOperator) {
      // For range operators, maintain min/max structure
      if (!newThresholds.min) newThresholds.min = {};
      if (!newThresholds.max) newThresholds.max = {};

      // Remove unselected axes from thresholds
      [1, 2, 3].forEach((axisVal) => {
        const axisKey = axisMap[axisVal];
        if (!newSelectedAxes.includes(axisVal)) {
          delete newThresholds.min[axisKey];
          delete newThresholds.max[axisKey];
        } else {
          if (newThresholds.min[axisKey] === undefined) {
            newThresholds.min[axisKey] = 0;
          }
          if (newThresholds.max[axisKey] === undefined) {
            newThresholds.max[axisKey] = 0;
          }
        }
      });
    } else {
      // For regular operators, remove unselected axes
      [1, 2, 3].forEach((axisVal) => {
        const axisKey = axisMap[axisVal];
        if (!newSelectedAxes.includes(axisVal)) {
          delete newThresholds[axisKey];
        } else if (newThresholds[axisKey] === undefined) {
          newThresholds[axisKey] = 0;
        }
      });
    }

    onUpdate({
      axis: newSelectedAxes,
      thresholds: newThresholds
    });
  }, [selectedAxes, isTriaxial, isRangeOperator, condition.thresholds, axisMap, onUpdate]);

  const handleOperatorChange = useCallback((e, data) => {
    const newOperator = data.value;
    const isNewRangeOperator = newOperator === 'OUTSIDE' || newOperator === 'INSIDE';

    // When switching between range and non-range operators, adjust threshold structure
    let newThresholds;
    if (isNewRangeOperator && !isRangeOperator) {
      // Switching to range operator - convert single values to min/max
      if (isTriaxial) {
        const minObj = {};
        const maxObj = {};
        selectedAxes.forEach((axisVal) => {
          const axisKey = axisMap[axisVal];
          const val = (condition.thresholds && condition.thresholds[axisKey]) || 0;
          minObj[axisKey] = 0;
          maxObj[axisKey] = val;
        });
        newThresholds = { min: minObj, max: maxObj };
      } else {
        const value = (condition.thresholds && condition.thresholds.value) || 0;
        newThresholds = {
          min: { value: 0 },
          max: { value }
        };
      }
    } else if (!isNewRangeOperator && isRangeOperator) {
      // Switching from range operator - use max values as single values
      if (isTriaxial) {
        const max = condition.thresholds && condition.thresholds.max;
        const newThresholdsObj = {};
        selectedAxes.forEach((axisVal) => {
          const axisKey = axisMap[axisVal];
          newThresholdsObj[axisKey] = (max && max[axisKey]) || 0;
        });
        newThresholds = newThresholdsObj;
      } else {
        const max = condition.thresholds && condition.thresholds.max;
        newThresholds = {
          value: (max && max.value) || 0
        };
      }
    } else {
      // Same operator type - preserve thresholds
      newThresholds = condition.thresholds;
    }

    onUpdate({ operator: newOperator, thresholds: newThresholds });
  }, [isRangeOperator, isTriaxial, selectedAxes, condition.thresholds, axisMap, onUpdate]);

  const handleThresholdChange = useCallback((axis, value) => {
    // Update local state immediately to allow empty values
    setThresholdInputs(prev => ({ ...prev, [axis]: value }));

    // Only update parent if value is a valid number
    const numValue = parseFloat(value);
    if (!Number.isNaN(numValue)) {
      if (isRangeOperator) {
        // Handle range operators
        if (isTriaxial) {
          // For triaxial range: axis will be minX, minY, minZ, maxX, maxY, maxZ
          const isMin = axis.startsWith('min');
          const actualAxis = axis.replace(/^(min|max)/, '').toLowerCase();
          const newThresholds = { ...condition.thresholds };

          if (!newThresholds.min) newThresholds.min = { x: 0, y: 0, z: 0 };
          if (!newThresholds.max) newThresholds.max = { x: 0, y: 0, z: 0 };

          if (isMin) {
            newThresholds.min[actualAxis] = numValue;
          } else {
            newThresholds.max[actualAxis] = numValue;
          }

          onUpdate({ thresholds: newThresholds });
        } else {
          // For single-axis range: axis will be minValue or maxValue
          const newThresholds = { ...condition.thresholds };
          if (!newThresholds.min) newThresholds.min = { value: 0 };
          if (!newThresholds.max) newThresholds.max = { value: 0 };

          if (axis === 'minValue') {
            newThresholds.min.value = numValue;
          } else {
            newThresholds.max.value = numValue;
          }

          onUpdate({ thresholds: newThresholds });
        }
      } else if (isTriaxial) {
        // Handle regular operators - triaxial
        onUpdate({
          thresholds: { ...condition.thresholds, [axis]: numValue }
        });
      } else {
        // Handle regular operators - single value
        onUpdate({
          thresholds: { value: numValue }
        });
      }
    }
  }, [isRangeOperator, isTriaxial, condition.thresholds, onUpdate, setThresholdInputs]);

  const handleThresholdBlur = useCallback((axis) => {
    // On blur, if input is empty, set to 0
    const currentValue = thresholdInputs[axis];
    if (currentValue === '' || currentValue === null || currentValue === undefined) {
      const numValue = 0;
      setThresholdInputs(prev => ({ ...prev, [axis]: '0' }));

      if (isRangeOperator) {
        // Handle range operators
        if (isTriaxial) {
          const isMin = axis.startsWith('min');
          const actualAxis = axis.replace(/^(min|max)/, '').toLowerCase();
          const newThresholds = { ...condition.thresholds };

          if (!newThresholds.min) newThresholds.min = { x: 0, y: 0, z: 0 };
          if (!newThresholds.max) newThresholds.max = { x: 0, y: 0, z: 0 };

          if (isMin) {
            newThresholds.min[actualAxis] = numValue;
          } else {
            newThresholds.max[actualAxis] = numValue;
          }

          onUpdate({ thresholds: newThresholds });
        } else {
          const newThresholds = { ...condition.thresholds };
          if (!newThresholds.min) newThresholds.min = { value: 0 };
          if (!newThresholds.max) newThresholds.max = { value: 0 };

          if (axis === 'minValue') {
            newThresholds.min.value = numValue;
          } else {
            newThresholds.max.value = numValue;
          }

          onUpdate({ thresholds: newThresholds });
        }
      } else if (isTriaxial) {
        // Handle regular operators - triaxial
        onUpdate({
          thresholds: { ...condition.thresholds, [axis]: numValue }
        });
      } else {
        // Handle regular operators - single value
        onUpdate({
          thresholds: { value: numValue }
        });
      }
    }
  }, [thresholdInputs, isRangeOperator, isTriaxial, condition.thresholds, onUpdate, setThresholdInputs]);

  return (
    <ConditionRowContainer hasError={hasError} isCoupled={isCoupled}>
      {isCoupled && (
        <div style={{ position: 'absolute', top: '8px', right: '36px' }}>
          <Popup
            trigger={(
              <LinkedBadge>
                <span>{t('sensors.eventDetection.coupling.linkedUpper')}</span>
              </LinkedBadge>
            )}
            content={t('sensors.eventDetection.coupling.conditionLinkedHelp')}
            position="bottom right"
            size="small"
          />
        </div>
      )}

      {showRemove && (
        <RemoveButton variant="ghost" size="sm" onClick={onRemove}>
          <Times />
        </RemoveButton>
      )}

      <DropdownContainer width="150px">
        <Dropdown
          selection
          fluid
          disabled={false}
          value={condition.metric}
          options={metrics.map((metric) => {
            const IconComponent = metric.icon;
            return {
              key: metric.key,
              text: (
                <IconText>
                  <IconComponent style={{ fontSize: '0.9em' }} />
                  {metric.text}
                </IconText>
              ),
              value: metric.value
            };
          })}
          onChange={handleMetricChange}
        />
      </DropdownContainer>

      <DropdownContainer width="150px">
        <Dropdown
          selection
          fluid
          disabled={false}
          value={condition.metricType}
          options={currentMetricTypeOptions}
          onChange={handleMetricTypeChange}
        />
      </DropdownContainer>

      <DropdownContainer width="100px">
        <Dropdown
          selection
          fluid
          disabled={isCoupled}
          value={condition.operator}
          options={operators}
          onChange={handleOperatorChange}
        />
      </DropdownContainer>

      {/* Axis selector for triaxial sensors */}
      {isTriaxial && (
        <AxisSelectorContainer>
          <ConditionLabel>{t('sensors.eventDetection.axes')}</ConditionLabel>
          <AxisToggleButtonGroup>
            {[1, 2, 3].map(axisVal => (
              <AxisToggleButton
                key={axisVal}
                selected={selectedAxes.includes(axisVal)}
                onClick={() => !isCoupled && handleAxisChange(axisVal)}
                type="button"
                disabled={isCoupled}
                style={{ cursor: isCoupled ? 'not-allowed' : 'pointer', opacity: isCoupled ? 0.6 : 1 }}
              >
                {axisLabels[axisVal]}
              </AxisToggleButton>
            ))}
          </AxisToggleButtonGroup>
        </AxisSelectorContainer>
      )}

      {(() => {
        if (isRangeOperator && isTriaxial) {
          return (
            <ConditionInputGroup>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'max-content auto auto',
                  gap: '8px 12px',
                  alignItems: 'center',
                  marginBottom: '4px'
                }}
              >
                {/* Headers */}
                <div />
                <RangeColumnHeader>{t('sensors.eventDetection.min')}</RangeColumnHeader>
                <RangeColumnHeader>{t('sensors.eventDetection.max')}</RangeColumnHeader>

                {/* Axis Rows */}
                {selectedAxes.map((axisVal) => {
                  const axisKey = axisMap[axisVal];
                  const minKey = `min${axisKey.toUpperCase()}`;
                  const maxKey = `max${axisKey.toUpperCase()}`;
                  return (
                    <React.Fragment key={axisVal}>
                      <ConditionLabel style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {axisLabels[axisVal]}:
                      </ConditionLabel>
                      <ResourceInputField
                        type="number"
                        value={thresholdInputs[minKey] || ''}
                        onChange={e => handleThresholdChange(minKey, e.target.value)}
                        onBlur={() => handleThresholdBlur(minKey)}
                        width="70px"
                        minwidth="60px"
                        disabled={isCoupled}
                      />
                      <ResourceInputField
                        type="number"
                        value={thresholdInputs[maxKey] || ''}
                        onChange={e => handleThresholdChange(maxKey, e.target.value)}
                        onBlur={() => handleThresholdBlur(maxKey)}
                        width="70px"
                        minwidth="60px"
                        disabled={isCoupled}
                      />
                    </React.Fragment>
                  );
                })}
              </div>
              <ConditionUnit style={{ alignSelf: 'flex-end', paddingBottom: '8px', marginLeft: '4px' }}>{unit}</ConditionUnit>
            </ConditionInputGroup>
          );
        }
        if (isRangeOperator) {
          return (
            <ConditionInputGroup>
              <ThresholdInputWrapper>
                <ConditionLabel>{t('sensors.eventDetection.min', { defaultValue: 'Min' })}:</ConditionLabel>
                <ResourceInputField
                  type="number"
                  value={thresholdInputs.minValue}
                  onChange={e => handleThresholdChange('minValue', e.target.value)}
                  onBlur={() => handleThresholdBlur('minValue')}
                  width="70px"
                  minwidth="60px"
                  disabled={isCoupled}
                />
              </ThresholdInputWrapper>
              <ThresholdInputWrapper>
                <ConditionLabel>{t('sensors.eventDetection.max', { defaultValue: 'Max' })}:</ConditionLabel>
                <ResourceInputField
                  type="number"
                  value={thresholdInputs.maxValue}
                  onChange={e => handleThresholdChange('maxValue', e.target.value)}
                  onBlur={() => handleThresholdBlur('maxValue')}
                  width="70px"
                  minwidth="60px"
                  disabled={isCoupled}
                />
              </ThresholdInputWrapper>
              <ConditionUnit>{unit}</ConditionUnit>
            </ConditionInputGroup>
          );
        }
        if (isTriaxial) {
          return (
            <ConditionInputGroup>
              {selectedAxes.map((axisVal) => {
                const axisKey = axisMap[axisVal];
                return (
                  <ThresholdInputWrapper key={axisVal}>
                    <ConditionLabel>{axisLabels[axisVal]}:</ConditionLabel>
                    <ResourceInputField
                      type="number"
                      value={thresholdInputs[axisKey] || ''}
                      onChange={e => handleThresholdChange(axisKey, e.target.value)}
                      onBlur={() => handleThresholdBlur(axisKey)}
                      width="70px"
                      minwidth="50px"
                      disabled={isCoupled}
                    />
                  </ThresholdInputWrapper>
                );
              })}
              <ConditionUnit>{unit}</ConditionUnit>
            </ConditionInputGroup>
          );
        }
        return (
          <ConditionInputGroup>
            <ResourceInputField
              type="number"
              value={thresholdInputs.value}
              onChange={e => handleThresholdChange('value', e.target.value)}
              onBlur={() => handleThresholdBlur('value')}
              width="80px"
              minwidth="60px"
              disabled={isCoupled}
            />
            <ConditionUnit>{unit}</ConditionUnit>
          </ConditionInputGroup>
        );
      })()}
    </ConditionRowContainer>
  );
};

ConditionRow.propTypes = {
  condition: PropTypes.shape({
    id: PropTypes.string,
    metric: PropTypes.string.isRequired,
    metricType: PropTypes.string.isRequired,
    operator: PropTypes.string.isRequired,
    thresholds: PropTypes.object.isRequired,
    axis: PropTypes.array
  }).isRequired,
  onUpdate: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  showRemove: PropTypes.bool.isRequired,
  eventMetadata: PropTypes.object,
  vibrationDirectionPreference: PropTypes.number,
  temperatureOnly: PropTypes.bool,
  magnetometerAvailable: PropTypes.bool,
  hasError: PropTypes.bool,
  isCoupled: PropTypes.bool
};

ConditionRow.defaultProps = {
  eventMetadata: null,
  vibrationDirectionPreference: 1,
  temperatureOnly: false,
  magnetometerAvailable: true,
  hasError: false,
  isCoupled: false
};

// Memoized because it has complex internal state and many handlers
// All handlers are now wrapped in useCallback for stable references
export default React.memo(ConditionRow);
